/** Shared booking session / visit-duration helpers (mirrors backend logic). */

export function isBookingClosed(b) {
  if (!b) return true;
  if (b.invoiced || b.paid_at || b.invoice_id) return true;
  const status = b.status || "confirmed";
  return status === "cancelled" || status === "no_show";
}

export function todayISO() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

export function isBookingStale(b, todayIso = todayISO()) {
  if (!b || isBookingClosed(b)) return false;
  const apptDate = b.date;
  if (!apptDate) return false;
  return apptDate < todayIso;
}

/** Cap live timers at end of appointment day for past unclosed bookings. */
export function sessionEndCapMs(b, nowMs = Date.now()) {
  if (!isBookingStale(b)) return nowMs;
  const endOfDay = new Date(`${b.date}T23:59:59`).getTime();
  return Math.min(nowMs, endOfDay);
}

export function bookingSessionState(b, todayIso = todayISO()) {
  if (!b) return "scheduled";
  if (b.status === "cancelled") return "cancelled";
  if (b.status === "no_show") return "no_show";
  if (b.invoiced || b.paid_at) return b.arrived_at ? "completed" : "missing_arrival";
  if (isBookingStale(b, todayIso)) return "stale";
  if (b.arrived_at && !b.service_started_at) return "waiting";
  if (b.service_started_at) return "in_service";
  if (b.arrived_at) return "in_progress";
  return "scheduled";
}

export function liveSessionMinutes(b, nowMs = Date.now()) {
  if (!b?.arrived_at) return { waiting: null, service: null, total: null, stale: isBookingStale(b) };
  const cap = sessionEndCapMs(b, nowMs);
  const arrived = new Date(b.arrived_at).getTime();
  const started = b.service_started_at ? new Date(b.service_started_at).getTime() : null;
  const paidTs = b.paid_at || b.invoiced_at;
  const paid = paidTs ? new Date(paidTs).getTime() : null;
  const effectiveEnd = paid || cap;

  let waiting = b.waiting_minutes;
  if (waiting == null && started) waiting = Math.max(0, Math.round((started - arrived) / 60000));
  else if (waiting == null && !started && !paid) waiting = Math.max(0, Math.round((effectiveEnd - arrived) / 60000));

  let service = b.service_duration_minutes;
  if (service == null && started) {
    service = Math.max(0, Math.round((effectiveEnd - started) / 60000));
  }

  let total = b.visit_duration_minutes;
  if (total == null) total = Math.max(0, Math.round((effectiveEnd - arrived) / 60000));

  return { waiting, service, total, stale: isBookingStale(b) };
}
